import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  errorMessage,
  onboardingStepId,
  responseTexts,
  storeSettingsSubsteps,
} from '@helpers/Constants';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { CustomerOrderService } from '@services/CustomerService';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { City, State, PostalCode } from '@helpers/Models';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { AxiosError } from 'axios';

// Maximum allowed characters for address lines
const MAX_ADDRESS_LENGTH = 100;

export const StoreSettingsAddress: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const { updateOnboardingStep } = useLocalStorage();
  const [sameAsStore, setSameAsStore] = useState(false);
  const [storeZipCode, setStoreZipCode] = useState('');
  const [billingZipCode, setBillingZipCode] = useState('');

  // Zip code error states
  const [storeZipError, setStoreZipError] = useState<string>('');
  const [billingZipError, setBillingZipError] = useState<string>('');

  // Address form fields
  const [storeAddress, setStoreAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });

  // API data for dropdowns (state, city, postal)
  const [storeAddressData, setStoreAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });
  const [billingAddressData, setBillingAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });

  const [companyDetails, setCompanyDetails] = useState<{
    companyName: string;
    userDetails: string;
  }>();

  // --- Backup states for billing when toggling "same as store" ---
  const [backupBillingAddress, setBackupBillingAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });
  const [backupBillingAddressData, setBackupBillingAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });
  const [backupBillingZipCode, setBackupBillingZipCode] = useState('');

  // --- Initial values for change detection ---
  const [initialStoreAddress, setInitialStoreAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });
  const [initialBillingAddress, setInitialBillingAddress] = useState({
    address1: '',
    address2: '',
    address3: '',
  });
  const [initialStoreZipCode, setInitialStoreZipCode] = useState('');
  const [initialBillingZipCode, setInitialBillingZipCode] = useState('');
  const [initialSameAsStore, setInitialSameAsStore] = useState(false);
  const [initialStoreAddressData, setInitialStoreAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });
  const [initialBillingAddressData, setInitialBillingAddressData] = useState<{
    states: State[];
    cities: City[];
    postalCodes: PostalCode[];
  }>({
    states: [],
    cities: [],
    postalCodes: [],
  });

  // --- Track if any changes were made ---
  const [hasChanges, setHasChanges] = useState(false);
  const [hasExistingAddress, setHasExistingAddress] = useState(false);

  // --- Fetch address data when component loads ---
  useEffect(() => {
    const fetchAddressData = async () => {
      try {
        console.log('Fetching address data...');
        const response = await Axios.get(ApiEndpoints.FETCH_ADDRESS);
        console.log('FETCH_ADDRESS response:', response.data);

        if (response.data?.data?.addresses) {
          const { storeAddress, billingAddress } = response.data.data.addresses;
          const companyName = response.data.data.addresses.companyName;
          const userDetails = response.data.data.userDetails.email;
          setCompanyDetails({ companyName, userDetails });

          // Determine if we have existing address data
          const hasStore = storeAddress && storeAddress.length > 0;
          const hasBilling = billingAddress && billingAddress.length > 0;
          setHasExistingAddress(hasStore || hasBilling);

          // Determine if billing is same as store
          const isAddressSame =
            hasStore &&
            hasBilling &&
            storeAddress[0].address1 === billingAddress[0].address1 &&
            storeAddress[0].address2 === billingAddress[0].address2 &&
            storeAddress[0].address3 === billingAddress[0].address3 &&
            storeAddress[0].cityId === billingAddress[0].cityId &&
            storeAddress[0].stateId === billingAddress[0].stateId &&
            storeAddress[0].postalCodeId === billingAddress[0].postalCodeId;

          setSameAsStore(isAddressSame);
          setInitialSameAsStore(isAddressSame);

          // Populate store address
          if (hasStore) {
            const storeAddr = {
              address1: storeAddress[0].address1 || '',
              address2: storeAddress[0].address2 || '',
              address3: storeAddress[0].address3 || '',
            };
            setStoreAddress(storeAddr);
            setInitialStoreAddress(storeAddr);

            // Fetch store address data (state, city, postal)
            await fetchAddressDataByIds(
              storeAddress[0].cityId,
              storeAddress[0].stateId,
              storeAddress[0].postalCodeId,
              'store'
            );
          }

          // Populate billing address
          if (hasBilling) {
            const billingAddr = {
              address1: billingAddress[0].address1 || '',
              address2: billingAddress[0].address2 || '',
              address3: billingAddress[0].address3 || '',
            };
            setBillingAddress(billingAddr);
            setInitialBillingAddress(billingAddr);
            // Backup billing data for separate billing
            setBackupBillingAddress(billingAddr);

            if (!isAddressSame) {
              // Fetch billing data only if not same as store
              await fetchAddressDataByIds(
                billingAddress[0].cityId,
                billingAddress[0].stateId,
                billingAddress[0].postalCodeId,
                'billing'
              );
            }
          }
        }
      } catch (error) {
        console.error('Error fetching address data:', error);
      }
    };

    fetchAddressData();
  }, []);

  // Helper to fetch address data by IDs (used for initial load)
  const fetchAddressDataByIds = async (
    cityId: number,
    stateId: number,
    postalCodeId: number,
    addressType: 'store' | 'billing'
  ) => {
    try {
      const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
        cityId,
        stateId,
        postalCodeId,
      });

      const { states, cities, postalCodes } = response.data.data;

      if (addressType === 'store') {
        const data = { states, cities, postalCodes };
        setStoreAddressData(data);
        setInitialStoreAddressData(data);
        if (postalCodes.length > 0) {
          const zip = postalCodes[0].postalCode;
          setStoreZipCode(zip);
          setInitialStoreZipCode(zip);
        }
        setStoreZipError(''); // clear any error
      } else {
        const data = { states, cities, postalCodes };
        setBillingAddressData(data);
        setInitialBillingAddressData(data);
        setBackupBillingAddressData(data);
        if (postalCodes.length > 0) {
          const zip = postalCodes[0].postalCode;
          setBillingZipCode(zip);
          setInitialBillingZipCode(zip);
          setBackupBillingZipCode(zip);
        }
        setBillingZipError(''); // clear any error
      }
    } catch (error) {
      console.error('Error fetching address data by IDs:', error);
      if (addressType === 'store') {
        setStoreZipError('Invalid postal code.');
        showToastMessage('Invalid postal code.', 'error');
      } else {
        setBillingZipError('Invalid postal code.');
        showToastMessage('Invalid postal code.', 'error');
      }
    }
  };

  // Sync billing data with store when sameAsStore is true and store data changes
  useEffect(() => {
    if (sameAsStore && storeAddressData.states.length > 0 && storeZipCode) {
      setBillingAddressData(storeAddressData);
      setBillingZipCode(storeZipCode);
      setInitialBillingAddressData(storeAddressData);
      setInitialBillingZipCode(storeZipCode);
      // Also set backup to empty because we don't have separate billing
      setBackupBillingAddress({ address1: '', address2: '', address3: '' });
      setBackupBillingAddressData({ states: [], cities: [], postalCodes: [] });
      setBackupBillingZipCode('');
    }
  }, [sameAsStore, storeAddressData, storeZipCode]);

  // --- Detect changes ---
  useEffect(() => {
    const hasStoreChanged =
      storeAddress.address1 !== initialStoreAddress.address1 ||
      storeAddress.address2 !== initialStoreAddress.address2 ||
      storeAddress.address3 !== initialStoreAddress.address3 ||
      storeZipCode !== initialStoreZipCode ||
      storeAddressData.states[0]?.id !== initialStoreAddressData.states[0]?.id ||
      storeAddressData.cities[0]?.id !== initialStoreAddressData.cities[0]?.id;

    const hasBillingChanged =
      billingAddress.address1 !== initialBillingAddress.address1 ||
      billingAddress.address2 !== initialBillingAddress.address2 ||
      billingAddress.address3 !== initialBillingAddress.address3 ||
      billingZipCode !== initialBillingZipCode ||
      billingAddressData.states[0]?.id !== initialBillingAddressData.states[0]?.id ||
      billingAddressData.cities[0]?.id !== initialBillingAddressData.cities[0]?.id;

    const sameAsStoreChanged = sameAsStore !== initialSameAsStore;

    setHasChanges(hasStoreChanged || hasBillingChanged || sameAsStoreChanged);
  }, [
    storeAddress,
    billingAddress,
    storeZipCode,
    billingZipCode,
    sameAsStore,
    storeAddressData,
    billingAddressData,
    initialStoreAddress,
    initialBillingAddress,
    initialStoreZipCode,
    initialBillingZipCode,
    initialSameAsStore,
    initialStoreAddressData,
    initialBillingAddressData,
  ]);

  // --- Validation function ---
  const validateForm = (): string | null => {
    // Store address validation
    if (!storeAddress.address1.trim()) {
      return 'Store Address Line 1 is required.';
    }
    if (storeAddress.address1.length > MAX_ADDRESS_LENGTH) {
      return `Store Address Line 1 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
    }
    if (storeAddress.address2.length > MAX_ADDRESS_LENGTH) {
      return `Store Address Line 2 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
    }
    if (storeAddress.address3.length > MAX_ADDRESS_LENGTH) {
      return `Store Address Line 3 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
    }

    // Store ZIP code validation
    if (!storeZipCode || storeZipCode.length !== 5) {
      return 'Store ZIP code is required and must be 5 digits.';
    }
    if (storeZipError) {
      return storeZipError;
    }

    // Billing address validation (only if not same as store)
    if (!sameAsStore) {
      if (!billingAddress.address1.trim()) {
        return 'Billing Address Line 1 is required.';
      }
      if (billingAddress.address1.length > MAX_ADDRESS_LENGTH) {
        return `Billing Address Line 1 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
      }
      if (billingAddress.address2.length > MAX_ADDRESS_LENGTH) {
        return `Billing Address Line 2 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
      }
      if (billingAddress.address3.length > MAX_ADDRESS_LENGTH) {
        return `Billing Address Line 3 cannot exceed ${MAX_ADDRESS_LENGTH} characters.`;
      }
      if (!billingZipCode || billingZipCode.length !== 5) {
        return 'Billing ZIP code is required and must be 5 digits.';
      }
      if (billingZipError) {
        return billingZipError;
      }
    }

    return null; // no error
  };

  // --- Handlers ---
  const handleSameAsStore = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      // Save current billing (if any) to backup before overwriting
      setBackupBillingAddress({ ...billingAddress });
      setBackupBillingAddressData({ ...billingAddressData });
      setBackupBillingZipCode(billingZipCode);
      // Copy store address to billing
      setBillingAddress({ ...storeAddress });
      setBillingZipCode(storeZipCode);
      setBillingAddressData({ ...storeAddressData });
      // Clear billing error as it's now same as store
      setBillingZipError('');
    } else {
      // Restore from backup; if backup empty, clear billing fields
      setBillingAddress(
        backupBillingAddress.address1 || backupBillingAddress.address2 || backupBillingAddress.address3
          ? { ...backupBillingAddress }
          : { address1: '', address2: '', address3: '' }
      );
      setBillingZipCode(backupBillingZipCode || '');
      setBillingAddressData(
        backupBillingAddressData.states.length > 0
          ? { ...backupBillingAddressData }
          : { states: [], cities: [], postalCodes: [] }
      );
    }
    setSameAsStore(checked);
  };

  const handleStoreAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStoreAddress((prev) => ({ ...prev, [name]: value }));
    if (sameAsStore) {
      setBillingAddress((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBillingAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleZipCodeChange = async (
    value: string,
    addressType: 'store' | 'billing'
  ) => {
    const zipCode = value.replace(/\D/g, '').slice(0, 5);

    // Clear previous error for this address type
    if (addressType === 'store') {
      setStoreZipError('');
    } else {
      setBillingZipError('');
    }

    if (addressType === 'store') {
      setStoreZipCode(zipCode);
      if (zipCode.length < 5) {
        setStoreAddressData({ states: [], cities: [], postalCodes: [] });
        if (sameAsStore) {
          setBillingAddressData({ states: [], cities: [], postalCodes: [] });
          setBillingZipCode('');
        }
      }
    } else {
      setBillingZipCode(zipCode);
      if (zipCode.length < 5) {
        setBillingAddressData({ states: [], cities: [], postalCodes: [] });
      }
    }

    if (zipCode.length === 5) {
      try {
        const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
          postalCode: zipCode,
        });
        const { states, cities, postalCodes } = response.data.data;
        if (addressType === 'store') {
          setStoreAddressData({ states, cities, postalCodes });
          setStoreZipError(''); // clear error
          if (sameAsStore) {
            setBillingAddressData({ states, cities, postalCodes });
            setBillingZipCode(zipCode);
            setBillingZipError(''); // clear error
          }
        } else {
          setBillingAddressData({ states, cities, postalCodes });
          setBillingZipError(''); // clear error
        }
        // Optionally show a success toast (not required)
        // showToastMessage('Address details loaded.', 'success');
      } catch (error) {
        console.error('Error fetching address data:', error);
        let errorMsg = 'Invalid postal code.';
        if (error instanceof AxiosError && error.response?.data?.message) {
          errorMsg = error.response.data.message;
        }

        if (addressType === 'store') {
          setStoreZipError(errorMsg);
          setStoreAddressData({ states: [], cities: [], postalCodes: [] });
          if (sameAsStore) {
            setBillingAddressData({ states: [], cities: [], postalCodes: [] });
            setBillingZipError(errorMsg);
          }
        } else {
          setBillingZipError(errorMsg);
          setBillingAddressData({ states: [], cities: [], postalCodes: [] });
        }

        // Show the error toast
        showToastMessage(errorMsg, 'error');
      }
    }
  };

  // --- Submit handler with navigation flag ---
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
    navigateAfterSave = false
  ) => {
    e.preventDefault();

    // Run client-side validation (including zip errors)
    const validationError = validateForm();
    if (validationError) {
      showToastMessage(validationError, 'error');
      return;
    }

    try {
      const payload = {
        storeAddress: {
          address1: storeAddress.address1,
          address2: storeAddress.address2,
          address3: storeAddress.address3,
          cityId: storeAddressData.cities[0]?.id || null,
          stateId: storeAddressData.states[0]?.id || null,
          postalCodeId: storeAddressData.postalCodes[0]?.id || null,
        },
        billingAddress: {
          address1: sameAsStore ? storeAddress.address1 : billingAddress.address1,
          address2: sameAsStore ? storeAddress.address2 : billingAddress.address2,
          address3: sameAsStore ? storeAddress.address3 : billingAddress.address3,
          cityId: sameAsStore
            ? storeAddressData.cities[0]?.id
            : billingAddressData.cities[0]?.id || null,
          stateId: sameAsStore
            ? storeAddressData.states[0]?.id
            : billingAddressData.states[0]?.id || null,
          postalCodeId: sameAsStore
            ? storeAddressData.postalCodes[0]?.id
            : billingAddressData.postalCodes[0]?.id || null,
        },
        sameAsStore,
      };

      const response = await Axios.post(ApiEndpoints.CREATE_ADDRESS, payload);

      // Update local storage with new address details
      const butcherDetails = localStorage.getItem('butcherDetails');
      let details = butcherDetails ? JSON.parse(butcherDetails) : {};
      details = {
        ...details,
        billingAddress: response.data.data.butcherDetails.billingAddress,
        storeAddress: response.data.data.butcherDetails.storeAddress,
      };
      CustomerOrderService.setButcherDetails(details);

      // Show appropriate success message
      const successMsg = hasExistingAddress
        ? 'Address updated successfully.'
        : responseTexts.ADDRESS_ADD_SUCCESS;
      showToastMessage(successMsg, 'success');

      // Mark that we now have an address
      setHasExistingAddress(true);

      updateOnboardingStep(onboardingStepId.ADDRESS);

      // Reset initial values to current to reflect saved state
      setInitialStoreAddress({ ...storeAddress });
      setInitialBillingAddress({ ...billingAddress });
      setInitialStoreZipCode(storeZipCode);
      setInitialBillingZipCode(billingZipCode);
      setInitialSameAsStore(sameAsStore);
      setInitialStoreAddressData({ ...storeAddressData });
      setInitialBillingAddressData({ ...billingAddressData });
      setHasChanges(false);

      if (navigateAfterSave) {
        navigate(FrontendRoutes.STORE_TAX);
      }
    } catch (error) {
      console.error('Error creating address:', error);
      // If the backend sends a specific message, show it; otherwise fallback
      const message =
        error instanceof AxiosError && error.response?.data?.message
          ? error.response.data.message
          : errorMessage.ERROR_CREATING_ADDRESS;
      showToastMessage(message, 'error');
    }
  };

  const getAddressLabel = (field: string) => {
    if (field === 'address1') return 'Address 1 *';
    if (field === 'address2') return 'Address 2';
    if (field === 'address3') return 'Address 3';
    return 'Address';
  };

  // --- Render content with fixed layout ---
  const renderContent = (
    <div className="flex flex-col min-h-screen">
      {/* Breadcrumbs */}
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Store"
          activeStep={2}
          subNavSteps={storeSettingsSubsteps}
          fromSettings={true}
          forStoreSetting={true}
        />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="w-full pt-4 font-sans">
          <span className="text-xl font-semibold ml-2 mb-4 block">Store Address</span>

          <form className="px-3 space-y-8">
            <div className="bg-white rounded-lg">
              <div className="space-y-2 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Store Name *
                  </label>
                  <span className="mb-2">{companyDetails?.companyName}</span>
                </div>
              </div>
              <div className="space-y-2 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <span className="mb-2">{companyDetails?.userDetails}</span>
                </div>
              </div>

              <div className="space-y-2" data-testid="store-address">
                {['address1', 'address2', 'address3'].map((field) => (
                  <div key={field}>
                    <label
                      htmlFor={`store-${field}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {getAddressLabel(field)}
                    </label>
                    <input
                      type="text"
                      id={`store-${field}`}
                      name={field}
                      value={storeAddress[field as keyof typeof storeAddress]}
                      onChange={handleStoreAddressChange}
                      placeholder="XYZ"
                      maxLength={MAX_ADDRESS_LENGTH}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-primary"
                    />
                  </div>
                ))}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label
                      htmlFor="store-postal-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Zip Code *
                    </label>
                    <input
                      type="text"
                      id="store-postal-input"
                      name="postalCodeInput"
                      value={storeZipCode}
                      onChange={(e) => handleZipCodeChange(e.target.value, 'store')}
                      placeholder="Enter 5-digit zip code"
                      maxLength={5}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-primary h-[38px] ${storeZipError ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {storeZipError && (
                      <p className="text-red-500 text-xs mt-1">{storeZipError}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="store-state"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      State *
                    </label>
                    <input
                      type="text"
                      id="store-state"
                      data-testid="store-state"
                      name="state"
                      value={storeAddressData.states[0]?.stateName || ''}
                      readOnly
                      placeholder="State will be filled automatically"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="store-city"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      City *
                    </label>
                    <input
                      type="text"
                      id="store-city"
                      name="city"
                      value={storeAddressData.cities[0]?.cityName || ''}
                      readOnly
                      placeholder="City will be filled automatically"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg">
              <h2 className="text-lg font-medium mb-4">Billing Address</h2>
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="same-as-store"
                  checked={sameAsStore}
                  onChange={handleSameAsStore}
                  className="w-4 h-4 text-primary hover:cursor-pointer accent-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="same-as-store" className="ml-2 text-sm text-gray-700">
                  My Billing Address is same as the Store Address
                </label>
              </div>
              {!sameAsStore && (
                <div className="space-y-4" data-testid="billing-address">
                  {['address1', 'address2', 'address3'].map((field) => (
                    <div key={field}>
                      <label
                        htmlFor={`billing-${field}`}
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {getAddressLabel(field)}
                      </label>
                      <input
                        type="text"
                        id={`billing-${field}`}
                        name={field}
                        value={billingAddress[field as keyof typeof billingAddress]}
                        onChange={handleBillingAddressChange}
                        placeholder="XYZ"
                        maxLength={MAX_ADDRESS_LENGTH}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-primary"
                      />
                    </div>
                  ))}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="billing-postal-input"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Zip Code *
                      </label>
                      <input
                        type="text"
                        id="billing-postal-input"
                        name="postalCodeInput"
                        value={billingZipCode}
                        onChange={(e) => handleZipCodeChange(e.target.value, 'billing')}
                        placeholder="Enter 5-digit zip code"
                        maxLength={5}
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-primary h-[38px] ${billingZipError ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {billingZipError && (
                        <p className="text-red-500 text-xs mt-1">{billingZipError}</p>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        htmlFor="billing-state"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        State *
                      </label>
                      <input
                        type="text"
                        id="billing-state"
                        name="state"
                        value={billingAddressData.states[0]?.stateName || ''}
                        readOnly
                        placeholder="State will be filled automatically"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="billing-city"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        City *
                      </label>
                      <input
                        type="text"
                        id="billing-city"
                        name="city"
                        value={billingAddressData.cities[0]?.cityName || ''}
                        readOnly
                        placeholder="City will be filled automatically"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 h-[38px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Sticky Footer – aligned with other pages */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 z-10 px-4 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-end gap-4">
          <button
            onClick={(e) => handleSubmit(e, false)}
            disabled={!hasChanges}
            className={`w-full sm:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasChanges
                ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
              }`}
            type="button"
          >
            Save
          </button>

          <button
            onClick={(e) => handleSubmit(e, true)}
            className="w-full sm:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 transition-colors duration-200"
            type="button"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );

  return <OnBoardSettings renderContent={renderContent} />;
};